import TextInput from '@porterin/component-library/src/base-components/text-input/TextInput';
import { convertPlaceToAddressDetails } from '@porterin/component-library/src/composite-components/google-auto-complete/GoogleAutoCompleteHelper';
import { Autocomplete } from '@react-google-maps/api';
import Image from 'next/image';
import React, { useState, useRef } from 'react';

import { clearIcon } from './store/Store';

type primaryAddress = {
  landmark: string;
  location: { lat: number; lng: number };
};

type postalAddress = {
  pincode?: string;
  state?: string;
  city?: string;
  locality?: string;
  sub_locality?: string;
};

export type AddressDetails = {
  primaryAddress: primaryAddress;
  postalAddress: postalAddress;
};

type inputOptions = {
  disabled?: boolean;
  placeholder?: string;
  error?: string;
};

type identifiers = {
  id: string;
};

type inputIcons = {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
};

type restrictions = {
  country: string;
};

type customValueState = {
  value: string;
  setValue: (new_val: string) => void;
};

interface GoogleMapsAutocompleteProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  identifiers: identifiers;
  onLocationSelect: (addressDetails: AddressDetails) => void;
  inputOptions?: inputOptions;
  icons?: inputIcons;
  restrictions: restrictions;
  className?: string;
  showClearIcon?: boolean;
  custom_value_state?: customValueState;
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
  identifiers,
  onLocationSelect,
  inputOptions,
  icons,
  restrictions = { country: 'IN' },
  className,
  showClearIcon = false,
  custom_value_state,
  ...props
}) => {
  const [internalValue, SetInternalValue] = useState<string>(
    String(props.value ?? ''),
  );
  const value = custom_value_state ? custom_value_state.value : internalValue;
  const setValue = custom_value_state
    ? custom_value_state.setValue
    : SetInternalValue;
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();

    if (!place || !place.geometry || !place.formatted_address) {
      console.warn('⚠️ Invalid place selected. Ignoring...');
      return;
    }
    setValue(place.formatted_address);

    const addressDetails = convertPlaceToAddressDetails(place);
    onLocationSelect(addressDetails);
  };

  const handleClearInput = () => {
    setValue('');
  };

  return (
    <Autocomplete
      onLoad={autocomplete => (autocompleteRef.current = autocomplete)}
      onPlaceChanged={handlePlaceSelect}
      options={{ componentRestrictions: { country: restrictions.country } }}
    >
      <TextInput
        ref={inputRef}
        identifiers={identifiers}
        interactions={{
          onChange: e => setValue(e.target.value),
        }}
        value={value}
        inputOptions={inputOptions}
        icons={{
          ...icons,
          rightIcon: showClearIcon ? (
            <div onClick={handleClearInput} id="clear-icon">
              <Image
                src={clearIcon.src}
                height={16}
                width={16}
                alt={clearIcon.alt}
              />
            </div>
          ) : null,
        }}
        className={className}
        {...props}
      />
    </Autocomplete>
  );
};

export default GoogleMapsAutocomplete;
